import { useState } from "react";
import { toast } from "sonner";
import { api, PART_TYPES, buildWhatsappLink, PHONE_NUMBER } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck, Truck, Wrench, HeadphonesIcon, ArrowRight, CheckCircle2, Phone,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const SERVICES = [
  {
    n: "01",
    title: "Motori Usati e Revisionati",
    desc: "Ampia selezione di motori testati e revisionati su banco prova certificato. Compatibilità verificata per ogni codice motore. Garanzia 12 mesi inclusa.",
    img: "https://images.unsplash.com/photo-1607603750909-408e193868c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bullets: ["Banco prova certificato", "Codici motore verificati", "Compressioni testate", "Garanzia 12 mesi"],
  },
  {
    n: "02",
    title: "Cambi Manuali e Automatici",
    desc: "Trasmissioni revisionate e testate sotto carico. Innesti fluidi, sincronizzatori controllati e oli ricambiati prima della spedizione.",
    img: "https://images.unsplash.com/photo-1711199694531-e982a79ea381?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bullets: ["Test sotto carico", "Sincronizzatori controllati", "Oli e filtri nuovi", "Garanzia 12 mesi"],
  },
];

const STANDARDS = [
  { icon: ShieldCheck, title: "Qualità Garantita", desc: "Ogni ricambio è testato e coperto da garanzia 12 mesi." },
  { icon: Wrench, title: "Affidabilità Tecnica", desc: "Esperti del settore con oltre 35 anni di esperienza automotive." },
  { icon: Truck, title: "Spedizioni Rapide", desc: "Consegne veloci e tracciate in tutta Italia con imballaggio sicuro." },
  { icon: HeadphonesIcon, title: "Supporto Pro", desc: "Assistenza dedicata per identificare il ricambio esatto per te." },
];

export default function HomePage() {
  return (
    <div>
      <Hero />
      <Standards />
      <Services />
      <RequestForm />
      <ProcessSteps />
    </div>
  );
}

const Hero = () => (
  <section className="relative overflow-hidden bg-slate-900 text-white">
    <div className="absolute inset-0">
      <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-900" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <div className="flex items-center gap-3 mb-8 fade-up">
          <div className="italic-flag" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-slate-300">
            Made in Italy · Dal 1992
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[0.95] tracking-tight fade-up"
          style={{ animationDelay: "0.05s" }}
        >
          Motori e Cambi<br/>
          <span className="italic text-red-500">usati e revisionati.</span>
        </h1>

        <p className="mt-8 text-lg lg:text-xl text-slate-300 max-w-xl leading-relaxed fade-up" style={{ animationDelay: "0.15s" }}>
          Affidati all'esperienza dei nostri tecnici. Ricambi testati su banco prova,
          garanzia 12 mesi e spedizione tracciata in tutta Italia.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 fade-up" style={{ animationDelay: "0.25s" }}>
          <a
            href="#richiesta"
            data-testid="hero-cta-richiesta"
            className="inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
          >
            Richiedi Preventivo <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`}
            data-testid="hero-cta-call"
            className="inline-flex items-center justify-center gap-3 bg-white text-slate-900 hover:bg-slate-100 px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
          >
            <Phone className="w-4 h-4" /> {PHONE_NUMBER}
          </a>
        </div>

        <div className="mt-14 grid grid-cols-3 gap-px bg-slate-700 border border-slate-700 max-w-xl fade-up" style={{ animationDelay: "0.35s" }}>
          {[
            { n: "12", l: "mesi garanzia" },
            { n: "35+", l: "anni esperienza" },
            { n: "24h", l: "per preventivo" },
          ].map((s) => (
            <div key={s.l} className="bg-slate-900 px-4 py-5">
              <div className="font-serif-display text-3xl lg:text-4xl text-white">{s.n}</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5 hidden lg:block">
        <div className="relative">
          <div className="absolute -top-4 -right-4 w-full h-full border-2 border-red-600/40"></div>
          <div className="relative bg-white text-slate-900 p-8 shadow-2xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-blue-900 mb-4">/ Garanzia inclusa</div>
            <h3 className="font-serif-display text-2xl font-medium leading-tight">
              "Banco prova certificato, motori e cambi testati prima della spedizione."
            </h3>
            <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Telefono</div>
                <div className="font-medium text-slate-900 mt-1">{PHONE_NUMBER}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Risposta</div>
                <div className="font-medium text-slate-900 mt-1">Entro 24h</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Standards = () => (
  <section id="perche-noi" className="py-24 lg:py-32 bg-slate-50 border-y border-slate-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="italic-flag" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-blue-900">/ 01 — Perché Noi</span>
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-slate-900">
            L'Eccellenza<br/>
            <span className="italic text-blue-900">Officina Franco.</span>
          </h2>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 flex items-end">
          <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
            Standard rigorosi, processi certificati e una passione meccanica tramandata
            da generazioni. Niente scorciatoie, solo precisione.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        {STANDARDS.map(({ icon: Icon, title, desc }, i) => (
          <div
            key={title}
            data-testid={`standard-card-${i + 1}`}
            className="group bg-white p-8 hover:bg-blue-900 transition-colors duration-300"
          >
            <div className="flex items-center justify-between mb-8">
              <span className="font-serif-display text-3xl text-slate-300 group-hover:text-red-500 transition-colors">
                0{i + 1}
              </span>
              <Icon className="w-5 h-5 text-blue-900 group-hover:text-white transition-colors" />
            </div>
            <h3 className="font-serif-display text-xl font-medium mb-3 text-slate-900 group-hover:text-white">
              {title}
            </h3>
            <p className="text-sm text-slate-600 group-hover:text-slate-300 leading-relaxed">
              {desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Services = () => (
  <section id="servizi" className="py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 mb-16">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <div className="italic-flag" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-blue-900">/ 02 — Servizi</span>
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-slate-900">
            Cosa <span className="italic text-blue-900">facciamo</span> per te.
          </h2>
        </div>
        <div className="lg:col-span-5 flex items-end">
          <p className="text-slate-600 leading-relaxed">
            Specializzati esclusivamente in motori e cambi di altissima qualità,
            con verifica tecnica completa prima della consegna.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {SERVICES.map((s) => (
          <article
            key={s.n}
            data-testid={`service-card-${s.n}`}
            className="group relative bg-white border border-slate-200 overflow-hidden hover:border-blue-900 transition-colors"
          >
            <div className="aspect-[16/10] overflow-hidden bg-slate-100">
              <img
                src={s.img}
                alt={s.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-8 lg:p-10">
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono text-xs tracking-[0.3em] text-blue-900">/ {s.n}</span>
                <span className="text-[10px] tracking-[0.25em] uppercase bg-red-600 text-white px-3 py-1">
                  Garanzia 12 mesi
                </span>
              </div>
              <h3 className="font-serif-display text-3xl lg:text-4xl font-light text-slate-900 mb-4 leading-tight">
                {s.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">{s.desc}</p>
              <ul className="mt-6 grid grid-cols-2 gap-y-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-blue-900" /> {b}
                  </li>
                ))}
              </ul>
              <a
                href="#richiesta"
                className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-red-600 hover:gap-3 transition-all"
              >
                Richiedi questo ricambio <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const RequestForm = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", engine_code: "",
    part_type: "", brand_model: "", message: "", privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) { toast.error("Accetta il trattamento dei dati personali"); return; }
    if (!form.name || !form.phone || !form.engine_code || !form.part_type) {
      toast.error("Compila tutti i campi obbligatori (*)"); return;
    }
    setSubmitting(true);
    try {
      const { privacy, ...payload } = form;
      const { data } = await api.post("/quotes", payload);
      setSubmitted(data);
      toast.success("Richiesta inviata!");
      setTimeout(() => {
        document.getElementById("richiesta")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o chiamaci direttamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="richiesta" className="py-24 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto text-red-500 mb-6" data-testid="success-icon" />
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="italic-flag" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-slate-300">Richiesta ricevuta</span>
            <div className="italic-flag" />
          </div>
          <h2 className="font-serif-display text-4xl lg:text-5xl font-light tracking-tight">
            Grazie, <span className="italic text-red-500" data-testid="success-name">{submitted.name.split(" ")[0]}.</span>
          </h2>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-xl mx-auto">
            Abbiamo ricevuto correttamente la tua richiesta per un
            <strong className="text-white"> {submitted.part_type === "motore" ? "motore" : "cambio"}</strong>.
            Il nostro team tecnico verificherà la disponibilità e ti ricontatterà entro
            <strong className="text-white"> 24 ore</strong> al numero che ci hai fornito.
          </p>
          {submitted.email_sent && (
            <p className="mt-4 text-sm text-emerald-400" data-testid="email-sent-confirm">
              ✓ Una email di conferma è stata inviata a {submitted.email}
            </p>
          )}
          {!submitted.email_sent && submitted.email && (
            <p className="mt-4 text-sm text-slate-400">
              Ti contatteremo direttamente all'indirizzo {submitted.email}.
            </p>
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={buildWhatsappLink(submitted.name, submitted.part_type, submitted.engine_code)}
              target="_blank"
              rel="noreferrer"
              data-testid="wa-followup"
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
            >
              Continua su WhatsApp
            </a>
            <button
              onClick={() => { setSubmitted(null); setForm({ name: "", phone: "", email: "", engine_code: "", part_type: "", brand_model: "", message: "", privacy: false }); }}
              data-testid="new-request"
              className="inline-flex items-center justify-center bg-transparent border border-slate-600 hover:border-white text-white px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
            >
              Nuova richiesta
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="richiesta" className="py-24 lg:py-32 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="italic-flag" />
              <span className="text-[11px] tracking-[0.3em] uppercase text-blue-900">/ 03 — Richiesta</span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-slate-900">
              Cerca il <br/>
              <span className="italic text-red-600">ricambio</span> giusto.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Compila il modulo con i dettagli del veicolo. Il nostro team verificherà
              la disponibilità e ti invierà un preventivo personalizzato entro <strong>24 ore</strong>.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Preventivo gratuito senza impegno",
                "Risposta tecnica entro 24h",
                "Garanzia 12 mesi su ogni ricambio",
                "Spedizione tracciata in tutta Italia",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm text-slate-700 border-t border-slate-200 pt-3">
                  <CheckCircle2 className="w-4 h-4 text-blue-900 shrink-0" /> {b}
                </li>
              ))}
            </ul>

            <div className="mt-10 bg-white p-6 border-l-4 border-red-600">
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Preferisci parlare?</div>
              <a
                href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`}
                className="font-serif-display text-2xl text-blue-900 hover:text-red-600 transition-colors"
              >
                {PHONE_NUMBER}
              </a>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="request-form"
            className="lg:col-span-6 lg:col-start-7 bg-white border border-slate-200 p-8 lg:p-10 shadow-sm space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FF label="Nome e Cognome *">
                <Input data-testid="form-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
              </FF>
              <FF label="Telefono *">
                <Input data-testid="form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
              </FF>
              <FF label="Email (per conferma)">
                <Input data-testid="form-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
              </FF>
              <FF label="Tipo di ricambio *">
                <Select value={form.part_type} onValueChange={(v) => update("part_type", v)}>
                  <SelectTrigger data-testid="form-part-type" className="rounded-none border-slate-300">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {PART_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FF>
              <FF label="Codice motore o targa *" full>
                <Input data-testid="form-engine-code" value={form.engine_code} onChange={(e) => update("engine_code", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
              </FF>
              <FF label="Marca e Modello" full>
                <Input data-testid="form-brand-model" placeholder="es. Audi A4 2.0 TDI 2013" value={form.brand_model} onChange={(e) => update("brand_model", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
              </FF>
              <FF label="Messaggio aggiuntivo" full>
                <Textarea data-testid="form-message" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:ring-1 focus-visible:ring-offset-0" />
              </FF>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="form-privacy"
                data-testid="form-privacy"
                checked={form.privacy}
                onCheckedChange={(v) => update("privacy", !!v)}
                className="rounded-none border-slate-400 data-[state=checked]:bg-blue-900 data-[state=checked]:border-blue-900 mt-0.5"
              />
              <label htmlFor="form-privacy" className="text-xs text-slate-600 leading-relaxed">
                Accetto il trattamento dei miei dati personali per ricevere un preventivo,
                secondo la Privacy Policy e il GDPR. *
              </label>
            </div>

            <Button
              type="submit"
              data-testid="form-submit"
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700 rounded-none h-14 text-xs tracking-[0.25em] uppercase"
            >
              {submitting ? "Invio in corso..." : "Invia richiesta"}
            </Button>
            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
              Riceverai una email automatica di conferma. Il nostro team ti contatterà entro 24 ore.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const FF = ({ label, children, full }) => (
  <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</Label>
    {children}
  </div>
);

const ProcessSteps = () => (
  <section className="py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="text-center mb-16">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="italic-flag" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-blue-900">/ 04 — Come funziona</span>
          <div className="italic-flag" />
        </div>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900">
          Tre passi, <span className="italic text-blue-900">zero pensieri.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { n: "01", t: "Invia la richiesta", d: "Compila il modulo con codice motore o targa. Risposta entro 24h." },
          { n: "02", t: "Ricevi il preventivo", d: "Ti contattiamo con disponibilità, prezzo e tempi di consegna." },
          { n: "03", t: "Consegna garantita", d: "Spedizione tracciata in tutta Italia con garanzia 12 mesi inclusa." },
        ].map((step, i) => (
          <div key={step.n} className="relative">
            <div className="font-serif-display text-7xl lg:text-8xl font-light text-red-600/15 leading-none mb-2">
              {step.n}
            </div>
            <h3 className="font-serif-display text-2xl font-medium text-slate-900 mb-3">{step.t}</h3>
            <p className="text-slate-600 leading-relaxed">{step.d}</p>
            {i < 2 && (
              <ArrowRight className="hidden md:block absolute top-12 -right-4 w-6 h-6 text-slate-300" />
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);
